import React from 'react';
import styled from 'styled-components';
import { Body1, Body5, Body7, Container, Modal } from '@ui';
import { FlatButton, GhostButton, TransparentFlatButton } from '@ui/buttons';
import { CloseButton } from '@ui/buttons/CloseButton';
import { SegmentedControl } from '@features/scoreSpec/SegmentedControl';
import { SpecSelect } from '@features/scoreBackfill/SpecSelect';
import { DateWindow, KNOWABLE_WINDOW_PRESETS } from '@features/scoreBackfill/DateWindow';
import {
  CompanyFilter,
  FEED_KINDS,
  FEED_KIND_SEGMENTS,
  FeedPicker,
  filterFeedsByKind,
  useCompanyResolution,
  useFeeds,
} from '@features/feedView';
import { fetchScoreSpecs } from '@services/scoreBackfills';
import { createTimeInstance } from '@utils/time';
import { fetchFeedInput } from './fetchFeedInput';
import {
  COPY,
  FEED_INPUTS_MAX_ENTRIES,
  FEED_INPUT_SOURCES,
  feedIdsForSpec,
  hasFeed,
  overBudget,
  totalBytes,
} from './feedInputs';

// The picker behind "Select Score Feed": which feed (named directly, or every feed a score spec
// publishes), over which knowable window, for which companies, with or without the spec's written
// reasoning — and the Attach that fetches the rows into form state.
//
// Everything on it is borrowed from the Feed View page's query form, wired the same way: the same
// presets, the same company resolution, the same kind filter. It is a modal rather than that form
// because the DAA form already has a question to ask and this is one attachment on it.
//
// Chrome follows DocumentSelectionModal (the CSV picker beside it): the shared Modal, a bold title,
// a close control top right. Not that component itself — it is keyed on document selection modes.

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PendingList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PendingRow = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const PendingRemove = styled(GhostButton)`
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  line-height: 1;
  color: ${({ theme }) => theme.themeColors.secondaryText};
`;

const CheckboxRow = styled.label`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const NativeCheckbox = styled.input.attrs({ type: 'checkbox' })`
  cursor: pointer;
  margin: 0;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

// Colors through a wrapper rather than styled(Body7): BaseText hands its own class to the span it
// renders, and a parent rule targeting .base-text wins outright (the FeedViewForm hint precedent).
const ErrorText = styled.div`
  .base-text,
  .base-text * {
    color: ${({ theme }) => theme.themeColors.error};
  }
`;

const SOURCE_SEGMENTS = [
  { key: FEED_INPUT_SOURCES.SPEC, label: COPY.sourceSpec },
  { key: FEED_INPUT_SOURCES.FEED, label: COPY.sourceFeed },
];

const FEED_ID_INPUT_ID = 'daa-feed-input-feed-id';

// The 1y preset, as DateWindow would resolve it: the upper bound is today, the lower one a year
// before. Set here so the chip reads as pressed on open — the widget has no default-preset prop.
const defaultWindow = () => {
  const to = createTimeInstance().toDate();
  const from = createTimeInstance(to).subtract(1, 'year').toDate();
  return { from, to };
};

const IDLE = { status: 'idle', index: 0, total: 0, message: null, notes: [] };

export const FeedInputModal = ({ onDismiss, items = [], onAttach, canUseSpecs = false, canReadSpecs = false }) => {
  const [modalProps] = React.useState({ maximizeWidth: true, forceHeight: true });

  const [source, setSource] = React.useState(canUseSpecs ? FEED_INPUT_SOURCES.SPEC : FEED_INPUT_SOURCES.FEED);
  const [feedKind, setFeedKind] = React.useState(FEED_KINDS.all);
  const [feedId, setFeedId] = React.useState('');
  const [specId, setSpecId] = React.useState('');
  // Feeds of the chosen spec the analyst took off the list. Reset when the spec changes.
  const [excludedFeedIds, setExcludedFeedIds] = React.useState([]);
  const [window, setWindow] = React.useState(defaultWindow);
  const [companies, setCompanies] = React.useState([]);
  const [includePayload, setIncludePayload] = React.useState(false);
  const [attach, setAttach] = React.useState(IDLE);

  // The spec catalog, fetched the first time the spec face shows and never otherwise: the route is
  // gated on its own grant, which is also what gates the face.
  const [specs, setSpecs] = React.useState(null);
  const [specsError, setSpecsError] = React.useState(null);
  const specsRequestedRef = React.useRef(false);

  const { feeds, isLoading: isLoadingFeeds, error: feedsError, reload: reloadFeeds } = useFeeds();

  const companyResolution = useCompanyResolution({ companies, onCompaniesChange: setCompanies });

  // The chips the form holds right now, readable from inside a long attach without a stale closure.
  const itemsRef = React.useRef(items);
  itemsRef.current = items;

  const controllerRef = React.useRef(null);
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      controllerRef.current?.abort();
    };
  }, []);

  const loadSpecs = React.useCallback(async () => {
    specsRequestedRef.current = true;
    setSpecsError(null);
    const { specs: list, error } = await fetchScoreSpecs();
    if (!isMountedRef.current) return;
    setSpecs(list || []);
    setSpecsError(error);
  }, []);

  React.useEffect(() => {
    if (source === FEED_INPUT_SOURCES.SPEC && canUseSpecs && !specsRequestedRef.current) loadSpecs();
  }, [source, canUseSpecs, loadSpecs]);

  const visibleFeeds = React.useMemo(() => filterFeedsByKind(feeds, feedKind), [feeds, feedKind]);
  const hiddenCount = (feeds || []).length - visibleFeeds.length;

  const specFeedIds = React.useMemo(() => feedIdsForSpec(specId, feeds), [specId, feeds]);
  const pendingFeedIds = React.useMemo(
    () => specFeedIds.filter((id) => !excludedFeedIds.includes(id)),
    [specFeedIds, excludedFeedIds]
  );

  const handleSpecChange = (next) => {
    setSpecId(next);
    setExcludedFeedIds([]);
    setAttach(IDLE);
  };

  const isRunning = attach.status === 'running';
  const atCapacity = items.length >= FEED_INPUTS_MAX_ENTRIES;

  const fail = (message, retryable = false) => setAttach({ ...IDLE, status: 'error', message, retryable });

  const handleAttach = async () => {
    if (isRunning) return;

    const list = source === FEED_INPUT_SOURCES.SPEC ? pendingFeedIds : [feedId.trim()].filter((id) => id.length > 0);
    if (!list.length) return fail(COPY.needFeed);
    if (itemsRef.current.length + list.length > FEED_INPUTS_MAX_ENTRIES) return fail(COPY.tooManyEntries);
    const duplicate = list.find((id) => hasFeed(itemsRef.current, id));
    if (duplicate) return fail(COPY.alreadyAttached(duplicate));

    // Text still in the company box is resolved first, the way the Feed View page's submit does it.
    const resolvedCompanies = companyResolution.hasPendingText ? await companyResolution.flush() : companies;
    if (!isMountedRef.current) return undefined;

    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    const notes = [];
    const attached = [];
    // The chips as they were when this attach started. The parent re-renders after every onAttach,
    // so by the next iteration itemsRef already holds what `attached` holds; reading it again would
    // count every feed this loop added twice against the budget.
    const base = itemsRef.current;
    for (let index = 0; index < list.length; index += 1) {
      const id = list[index];
      setAttach({ status: 'running', index: index + 1, total: list.length, message: null, notes: [] });

      // eslint-disable-next-line no-await-in-loop
      const result = await fetchFeedInput({
        feedId: id,
        knowableFrom: window.from,
        knowableTo: window.to,
        companies: resolvedCompanies,
        includePayload,
        canReadSpecs,
        signal: controller.signal,
      });
      if (!isMountedRef.current || result.aborted) return undefined;
      if (result.error) return fail(result.error, result.retryable);

      const known = [...base, ...attached];
      if (overBudget(known, result.item.bytes)) return fail(COPY.overBudget(result.item.bytes, totalBytes(known)));

      if (result.item.entry.row_count === 0) notes.push(COPY.zeroRows(id));
      if (result.droppedColumns.length) notes.push(COPY.droppedColumns(id, result.droppedColumns));
      if (result.columnsRefused) notes.push(COPY.columnsRefused(id));

      onAttach(result.item);
      attached.push(result.item);
    }
    controllerRef.current = null;

    setAttach({
      status: 'done',
      index: list.length,
      total: list.length,
      message: COPY.attached(attached.length),
      notes,
    });
    if (source === FEED_INPUT_SOURCES.FEED) setFeedId('');
    return undefined;
  };

  const handleFeedKeyDown = (e) => {
    if (e.key === 'Enter') handleAttach();
  };

  return (
    <Modal onDismiss={onDismiss} modalProps={modalProps}>
      <Container row verticallyCenter spaceBetween>
        <Container>
          <Body1 bold>{COPY.modalTitle}</Body1>
        </Container>
        <Container row verticallyCenter>
          <TransparentFlatButton onClick={onDismiss}>{COPY.done}</TransparentFlatButton>
          <Container left={8}>
            <CloseButton onClick={onDismiss} />
          </Container>
        </Container>
      </Container>

      <Container top={24}>
        <Sections>
          {canUseSpecs && (
            <Section>
              <SegmentedControl
                label={COPY.sourceLabel}
                options={SOURCE_SEGMENTS}
                active={source}
                onSelect={(next) => {
                  setSource(next);
                  setAttach(IDLE);
                }}
                segmentWidth={120}
              />
            </Section>
          )}

          {source === FEED_INPUT_SOURCES.SPEC ? (
            <Section>
              <Body5 bold>{COPY.specLabel}</Body5>
              <Body7 isLowContrast>{COPY.specHint}</Body7>
              {specsError ? (
                <ErrorText>
                  <Body7>{specsError}</Body7>
                  <TransparentFlatButton onClick={loadSpecs}>{COPY.tryAgain}</TransparentFlatButton>
                </ErrorText>
              ) : (
                <SpecSelect specs={specs} value={specId} onChange={handleSpecChange} />
              )}
              {specId && isLoadingFeeds && <Body7 isLowContrast>{COPY.loadingFeeds}</Body7>}
              {specId && !isLoadingFeeds && specFeedIds.length === 0 && (
                <Body7 isLowContrast>{COPY.noFeedForSpec}</Body7>
              )}
              {pendingFeedIds.length > 0 && (
                <PendingList aria-label={COPY.pendingFeedsLabel}>
                  {pendingFeedIds.map((id) => (
                    <PendingRow key={id}>
                      <Body5>{id}</Body5>
                      <PendingRemove
                        aria-label={COPY.removeChip(id)}
                        onClick={() => setExcludedFeedIds((prev) => [...prev, id])}
                      >
                        <span aria-hidden="true">×</span>
                      </PendingRemove>
                    </PendingRow>
                  ))}
                </PendingList>
              )}
            </Section>
          ) : (
            <Section>
              <Body5 bold>
                <label htmlFor={FEED_ID_INPUT_ID}>{COPY.feedLabel}</label>
              </Body5>
              <SegmentedControl
                label={COPY.feedKindLabel}
                options={FEED_KIND_SEGMENTS}
                active={feedKind}
                onSelect={setFeedKind}
                segmentWidth={104}
              />
              <FeedPicker
                id={FEED_ID_INPUT_ID}
                value={feedId}
                onChange={setFeedId}
                onKeyDown={handleFeedKeyDown}
                feeds={visibleFeeds}
                isLoading={isLoadingFeeds}
                error={feedsError}
                onRetry={reloadFeeds}
                hiddenCount={hiddenCount}
                onShowAll={() => setFeedKind(FEED_KINDS.all)}
              />
            </Section>
          )}

          <Section>
            <Body5 bold>{COPY.windowLabel}</Body5>
            <Body7 isLowContrast>{COPY.windowHint}</Body7>
            <DateWindow
              fromDate={window.from}
              toDate={window.to}
              onFromChange={(d) => setWindow((prev) => ({ ...prev, from: d }))}
              onToChange={(d) => setWindow((prev) => ({ ...prev, to: d }))}
              maxDate={createTimeInstance().toDate()}
              presets={KNOWABLE_WINDOW_PRESETS}
              legend={COPY.windowLabel}
            />
          </Section>

          <Section>
            <Body5 bold>{COPY.companiesLabel}</Body5>
            <Body7 isLowContrast>{COPY.companiesHint}</Body7>
            <CompanyFilter
              companies={companies}
              onChange={setCompanies}
              text={companyResolution.text}
              onTextChange={companyResolution.setText}
              onResolve={companyResolution.flush}
              isResolving={companyResolution.isResolving}
              progress={companyResolution.progress}
              result={companyResolution.result}
              onDismissResult={companyResolution.dismiss}
            />
          </Section>

          <Section>
            {/* A native checkbox inside its label, the way NoRetrievalToggle beside this form does it:
                the shared @ui Checkbox is a div with no role, so nothing could name or press it. */}
            <CheckboxRow>
              <NativeCheckbox checked={includePayload} onChange={(e) => setIncludePayload(e.target.checked)} />
              <Body5 bold>{COPY.payloadCheckbox}</Body5>
            </CheckboxRow>
            {/* Outside the label so the control's accessible name is the title alone. */}
            <Container left={24}>
              <Body7 isLowContrast>{COPY.payloadHint}</Body7>
            </Container>
          </Section>

          <Section>
            <Footer>
              {/* The shared button's `disabled` is styling only; the guard is in the handler. */}
              <FlatButton onClick={handleAttach} disabled={isRunning || atCapacity}>
                {isRunning ? COPY.attaching(attach.index, attach.total) : COPY.attach}
              </FlatButton>
              {atCapacity && <Body7 isLowContrast>{COPY.tooManyEntries}</Body7>}
            </Footer>
            {/* Plain text rather than @ui's Working: that reads the colour theme through Redux, and this
                modal is otherwise free of it. The button's own label already says which read is running. */}
            {isRunning && (
              <Body7 isLowContrast role="status">
                {COPY.fetching}…
              </Body7>
            )}
            {attach.status === 'error' && (
              <ErrorText role="alert">
                <Body7>{attach.message}</Body7>
              </ErrorText>
            )}
            {attach.status === 'done' && (
              <div role="status">
                <Body7 isLowContrast>{attach.message}</Body7>
                {attach.notes.map((note) => (
                  <Container key={note} top={4}>
                    <Body7 isLowContrast>{note}</Body7>
                  </Container>
                ))}
              </div>
            )}
          </Section>
        </Sections>
      </Container>
    </Modal>
  );
};
